import type {
  AnyEquationDocument,
  AnyEvaluationResult,
  EquationDiagnostic,
  EquationDiagnosticCode,
  EquationDocument,
  EquationMatrixCheckpoint,
  EvaluationResult,
} from "@src/features/equation-editor/equation";
import {
  createEvaluationAbortError,
  type EquationEvaluationOptions,
  type EquationEvaluator,
} from "./evaluator-contract";
import type { Dimension } from "@src/math/types";
import type { EquationExpressionLocation } from "./evaluation-engine";
import type { AnyEvaluationWorkerRequest } from "./worker-messages";
import { copyEquationDocument } from "@src/features/equation-editor/equation-document";
import { parseEvaluationWorkerResponse } from "./worker-response-parser";

export const EQUATION_EVALUATION_TIMEOUT_MS = 750;
export const EQUATION_WORKER_STARTUP_TIMEOUT_MS = 10_000;

interface EvaluationWorkerLike {
  onmessage: ((event: MessageEvent<unknown>) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  postMessage(message: AnyEvaluationWorkerRequest): void;
  terminate(): void;
}

export type EvaluationWorkerFactory = () => EvaluationWorkerLike;

interface EvaluationClientPolicy {
  readonly executionTimeoutMs?: number;
  readonly startupTimeoutMs?: number;
}

export function createWorkerEvaluator(
  createWorker: EvaluationWorkerFactory = createBrowserWorker,
  policy: EvaluationClientPolicy = {},
): EquationEvaluator {
  const executionTimeoutMs =
    policy.executionTimeoutMs ?? EQUATION_EVALUATION_TIMEOUT_MS;
  const startupTimeoutMs =
    policy.startupTimeoutMs ?? EQUATION_WORKER_STARTUP_TIMEOUT_MS;
  let client2: EvaluationClient<2> | undefined;
  let client3: EvaluationClient<3> | undefined;
  function evaluate(
    document: EquationDocument<2>,
    options?: EquationEvaluationOptions,
  ): Promise<EvaluationResult<2>>;
  function evaluate(
    document: EquationDocument<3>,
    options?: EquationEvaluationOptions,
  ): Promise<EvaluationResult<3>>;
  function evaluate(
    document: AnyEquationDocument,
    options: EquationEvaluationOptions = {},
  ): Promise<AnyEvaluationResult> {
    if (document.dimension === 2) {
      client2 ??= new EvaluationClient(
        createWorker,
        executionTimeoutMs,
        startupTimeoutMs,
        (worker, requestDocument) => {
          worker.postMessage({ type: "evaluate", document: requestDocument });
        },
      );
      return client2.evaluate(document, options);
    }
    client3 ??= new EvaluationClient(
      createWorker,
      executionTimeoutMs,
      startupTimeoutMs,
      (worker, requestDocument) => {
        worker.postMessage({ type: "evaluate", document: requestDocument });
      },
    );
    return client3.evaluate(document, options);
  }
  return evaluate;
}

class EvaluationClient<D extends Dimension> {
  private worker: EvaluationWorkerLike | null = null;
  private state: EvaluationClientState<D> = { kind: "idle" };
  private readonly queue: EvaluationRequest<D>[] = [];
  private pumping = false;

  constructor(
    private readonly createWorker: EvaluationWorkerFactory,
    private readonly executionTimeoutMs: number,
    private readonly startupTimeoutMs: number,
    private readonly postDocument: (
      worker: EvaluationWorkerLike,
      document: EquationDocument<D>,
    ) => void,
  ) {}

  evaluate(
    document: EquationDocument<D>,
    options: EquationEvaluationOptions,
  ): Promise<EvaluationResult<D>> {
    const { signal } = options;
    if (signal?.aborted) return Promise.reject(createEvaluationAbortError());
    const requestDocument = copyEquationDocument(document);
    let resolve!: (result: EvaluationResult<D>) => void;
    let reject!: (error: Error) => void;
    const promise = new Promise<EvaluationResult<D>>(
      (resolvePromise, rejectPromise) => {
        resolve = resolvePromise;
        reject = rejectPromise;
      },
    );
    const request: EvaluationRequest<D> = {
      document: requestDocument,
      signal,
      progress: { location: null, checkpoint: null },
      resolve,
      reject,
      abort: () => {
        this.abort(request);
      },
    };
    signal?.addEventListener("abort", request.abort, { once: true });
    if (signal?.aborted) {
      this.cleanupRequest(request);
      reject(createEvaluationAbortError());
    } else {
      this.queue.push(request);
      this.pump();
    }
    return promise;
  }

  private abort(request: EvaluationRequest<D>): void {
    if (this.state.kind !== "idle" && this.state.request === request) {
      this.discardWorker();
      this.rejectActive(request, createEvaluationAbortError());
      return;
    }
    const index = this.queue.indexOf(request);
    if (index < 0) return;
    this.queue.splice(index, 1);
    this.cleanupRequest(request);
    request.reject(createEvaluationAbortError());
    this.pump();
  }

  private resolve(
    request: EvaluationRequest<D>,
    result: EvaluationResult<D>,
  ): void {
    this.releaseActive(request);
    request.resolve(result);
    this.pump();
  }

  private rejectActive(request: EvaluationRequest<D>, error: Error): void {
    this.releaseActive(request);
    request.reject(error);
    this.pump();
  }

  private releaseActive(request: EvaluationRequest<D>): void {
    /* v8 ignore next -- private state transitions only release active requests */
    if (this.state.kind === "idle" || this.state.request !== request)
      throw new Error("Cannot release an inactive evaluation request");
    if (this.state.kind !== "creating") clearTimeout(this.state.timer);
    this.state = { kind: "idle" };
    this.cleanupRequest(request);
  }

  private cleanupRequest(request: EvaluationRequest<D>): void {
    request.signal?.removeEventListener("abort", request.abort);
  }

  private failActive(
    code: "evaluation-timeout" | "evaluation-unavailable",
  ): void {
    /* v8 ignore next -- callbacks are detached before the client becomes idle */
    if (this.state.kind === "idle") return;
    const request = this.state.request;
    this.discardWorker();
    this.resolve(
      request,
      failureResult(
        code,
        request.progress.location,
        request.progress.checkpoint,
      ),
    );
  }

  private handleMessage(data: unknown): void {
    if (this.state.kind === "idle") return;
    const request = this.state.request;
    try {
      const response = parseEvaluationWorkerResponse(data, request.document);
      switch (response.type) {
        case "started":
          this.handleStarted(request);
          break;
        case "expression":
          request.progress = {
            ...request.progress,
            location: response.location,
          };
          break;
        case "expression-complete":
          request.progress = { ...request.progress, location: null };
          break;
        case "matrix":
          request.progress = {
            ...request.progress,
            checkpoint: response.checkpoint,
          };
          break;
        case "result":
          this.resolve(request, response.result);
          break;
        case "error":
          this.failActive("evaluation-unavailable");
          break;
      }
    } catch {
      this.failActive("evaluation-unavailable");
    }
  }

  private handleStarted(request: EvaluationRequest<D>): void {
    if (this.state.kind !== "starting" || this.state.request !== request)
      return;
    clearTimeout(this.state.timer);
    const timer = setTimeout(() => {
      this.handleExecutionTimeout(request);
    }, this.executionTimeoutMs);
    this.state = { kind: "running", request, timer };
  }

  private handleExecutionTimeout(request: EvaluationRequest<D>): void {
    if (this.state.kind === "running" && this.state.request === request)
      this.failActive("evaluation-timeout");
  }

  private ensureWorker(): EvaluationWorkerLike | null {
    if (this.worker !== null) return this.worker;
    const created = tryCreateWorker(this.createWorker);
    if (created === null) return null;
    this.worker = created;
    created.onmessage = ({ data }) => {
      if (this.worker === created) this.handleMessage(data);
    };
    created.onerror = () => {
      this.handleWorkerError(created);
    };
    return created;
  }

  private handleWorkerError(failed: EvaluationWorkerLike): void {
    if (this.worker !== failed) return;
    if (this.state.kind === "idle") this.discardWorker();
    else this.failActive("evaluation-unavailable");
  }

  private discardWorker(): void {
    const discarded = this.worker;
    if (discarded === null) return;
    this.worker = null;
    discarded.onmessage = null;
    discarded.onerror = null;
    discarded.terminate();
  }

  private pump(): void {
    if (this.pumping) return;
    this.pumping = true;
    try {
      this.pumpUntilActive();
    } finally {
      this.pumping = false;
    }
  }

  private pumpUntilActive(): void {
    while (this.state.kind === "idle") {
      const request = this.nextQueuedRequest();
      if (request === undefined) return;
      this.start(request);
    }
  }

  private nextQueuedRequest(): EvaluationRequest<D> | undefined {
    return this.queue.shift();
  }

  private start(request: EvaluationRequest<D>): void {
    this.state = { kind: "creating", request };
    const worker = this.ensureWorker();
    if (!this.isCreating(request)) return;
    if (worker === null) {
      this.state = { kind: "idle" };
      this.cleanupRequest(request);
      request.resolve(failureResult("evaluation-unavailable", null, null));
      this.pump();
      return;
    }
    this.post(request, worker);
  }

  private isCreating(request: EvaluationRequest<D>): boolean {
    return this.state.kind === "creating" && this.state.request === request;
  }

  private post(
    request: EvaluationRequest<D>,
    worker: EvaluationWorkerLike,
  ): void {
    const timer = setTimeout(() => {
      this.handleStartupTimeout(request);
    }, this.startupTimeoutMs);
    this.state = { kind: "starting", request, timer };
    try {
      this.postDocument(worker, request.document);
    } catch {
      this.failActive("evaluation-unavailable");
    }
  }

  private handleStartupTimeout(request: EvaluationRequest<D>): void {
    if (this.state.kind === "starting" && this.state.request === request)
      this.failActive("evaluation-unavailable");
  }
}

interface EvaluationRequest<D extends Dimension> {
  readonly document: EquationDocument<D>;
  readonly signal: AbortSignal | undefined;
  progress: EvaluationProgress<D>;
  readonly resolve: (result: EvaluationResult<D>) => void;
  readonly reject: (error: Error) => void;
  readonly abort: () => void;
}

interface EvaluationProgress<D extends Dimension> {
  readonly location: EquationExpressionLocation | null;
  readonly checkpoint: EquationMatrixCheckpoint<D> | null;
}

type EvaluationClientState<D extends Dimension> =
  | { readonly kind: "idle" }
  | { readonly kind: "creating"; readonly request: EvaluationRequest<D> }
  | {
      readonly kind: "starting" | "running";
      readonly request: EvaluationRequest<D>;
      readonly timer: ReturnType<typeof setTimeout>;
    };
export const evaluateEquationInWorker: EquationEvaluator =
  createWorkerEvaluator();

function tryCreateWorker(
  createWorker: EvaluationWorkerFactory,
): EvaluationWorkerLike | null {
  try {
    return createWorker();
  } catch {
    return null;
  }
}

function createBrowserWorker(): EvaluationWorkerLike {
  if (typeof Worker === "undefined")
    throw new Error("Web Workers are unavailable");
  return new Worker(new URL("./evaluation.worker.ts", import.meta.url), {
    type: "module",
  });
}

function failureResult<D extends Dimension>(
  code: Extract<
    EquationDiagnosticCode,
    "evaluation-timeout" | "evaluation-unavailable"
  >,
  location: EquationExpressionLocation | null,
  checkpoint: EquationMatrixCheckpoint<D> | null,
): EvaluationResult<D> {
  const diagnostic: EquationDiagnostic = location
    ? { code, location }
    : { code };
  return {
    kind: "invalid",
    diagnostics: [diagnostic],
    ...(checkpoint ? { checkpoint } : {}),
  };
}
